import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import CartSidebar from './CartSidebar';
import AuthModal from './auth/AuthModal';
import { AUTH_ENABLED, CART_ENABLED } from '../config/features';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { cart, isOpen, setIsOpen } = useCart();
  const { user, signOut } = useAuth();
  const location = useLocation();



  const handleSignOut = async () => {
    try {
      await signOut();
      setIsUserMenuOpen(false);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const navigation = [
    { name: 'Магазин', href: '/shop' },
    { name: 'Продукция', href: '/products' },
    { name: 'О нас', href: '/about' },
    { name: 'Доставка/Оплата', href: '/delivery' },
  ];

  return (
    <>
      <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Логотип */}
            <Link to="/" className="flex items-center">
              <div className="flex items-center space-x-2">
                <span className="text-xl font-bold">
                  <span className="text-accent-purple">ПРО</span>
                  <span className="text-accent-purple">Т</span>
                  <span className="text-accent-green">ЭКО</span>
                </span>
                <svg className="w-8 h-8" viewBox="0 0 581.102 581.102" xmlns="http://www.w3.org/2000/svg">
                  <g id="layer_1" data-name="Layer">
                    <g clipPath="url(#clip_1)">
                      <path transform="matrix(1,0,0,-1,480.7455,568.12869)" d="M0 0H-380.5C-406.622 0-430.296 10.62-447.436 27.731-464.608 44.889-475.241 68.587-475.241 94.694V455.342C-475.241 481.496-464.626 505.147-447.486 522.305-430.315 539.463-406.641 550.129-380.5 550.129H0C26.122 550.129 49.815 539.463 66.986 522.305 84.144 505.147 94.718 481.496 94.718 455.342V94.694C94.718 68.587 84.098 44.889 66.931 27.731 49.796 10.62 26.122 0 0 0M-380.5 22.213H0C20.052 22.213 38.184 30.328 51.317 43.452 64.413 56.529 72.514 74.661 72.514 94.694V455.342C72.514 475.422 64.398 493.554 51.28 506.678 38.184 519.801 20.052 527.917 0 527.917H-380.5C-400.552 527.917-418.684 519.801-431.784 506.678-444.899 493.554-453.033 475.422-453.033 455.342V94.694C-453.033 74.661-444.917 56.529-431.817 43.452-418.703 30.328-400.552 22.213-380.5 22.213" fill="#2fb474" fillRule="evenodd"/>
                      <path transform="matrix(1,0,0,-1,304.6695,111.39548)" d="M0 0C0 26.479-21.485 47.996-48.001 47.996-74.504 47.996-96.007 26.479-96.007 0-96.007-26.526-74.504-48.044-48.001-48.044-21.485-48.044 0-26.526 0 0" fill="#000205" fillRule="evenodd"/>
                      <path transform="matrix(1,0,0,-1,204.2984,245.9252)" d="M0 0C0-108.654-73.655-186.282-73.655-186.282-73.655-186.282-147.31-108.654-147.31 0-147.31 108.607-73.655 196.716-73.655 196.716-73.655 196.716 0 108.607 0 0" fill="#2fb474" fillRule="evenodd"/>
                      <path transform="matrix(1,0,0,-1,379.9708,331.8092)" d="M0 0C-82.545-66.361-160.401-36.542-160.401-36.542-160.401-36.542-181.849 48.229-99.304 114.543-16.755 180.903 130.607 197.366 130.607 197.366 130.607 197.366 82.55 66.314 0 0" fill="#2fb474" fillRule="evenodd"/>
                      <path transform="matrix(1,0,0,-1,351.6043,526.0681)" d="M0 0C-110.443-20.682-207.322 57.828-207.322 57.828-207.322 57.828-139.918 125.534-29.47 146.216 80.973 166.899 187.089 122.101 187.089 122.101 187.089 122.101 110.448 20.682 0 0" fill="#2fb474" fillRule="evenodd"/>
                    </g>
                  </g>
                </svg>
              </div>
            </Link>

            {/* Навигация для десктопа */}
            <nav className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Правая часть */}
            <div className="flex items-center space-x-4">
              {/* Корзина */}
              {CART_ENABLED && (
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="relative p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  <ShoppingCart className="w-6 h-6" />
                  {cart.items.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {cart.items.reduce((total, item) => total + item.quantity, 0)}
                    </span>
                  )}
                </button>
              )}

              {/* Личный кабинет - HOVER для авторизованных, CLICK для неавторизованных */}
              {AUTH_ENABLED && (
                <div
                  className="relative cursor-pointer p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                  onMouseEnter={() => {
                    if (user) {
                      setIsUserMenuOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (user) {
                      setIsUserMenuOpen(false);
                    }
                  }}
                  onClick={() => {
                    if (!user) {
                      setAuthMode('login');
                      setIsAuthModalOpen(true);
                    }
                  }}
                >
                  {user ? (
                    <div className="w-6 h-6">
                      <svg viewBox="0 0 800 800" className="w-full h-full text-primary-600 dark:text-primary-400">
                        <path
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth="40"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M 499.996674 508.48999 C 462.203339 482.126678 416.23999 466.666656 366.66333 466.666656 C 246.799667 466.666656 148.045334 557.046631 134.832672 673.376648 C 133.897675 681.609985 133.430008 685.726685 135.073669 689.703308 C 136.418655 692.953369 139.330002 696.213318 142.409988 697.916687 C 146.173996 700 150.781998 700 159.99733 700 L 331.488312 700 M 466.66333 642.856689 L 526.66333 700 L 666.66333 566.666687 M 499.996674 233.333313 C 499.996674 306.971313 440.303314 366.666656 366.66333 366.666656 C 293.026001 366.666656 233.330658 306.971313 233.330658 233.333313 C 233.330658 159.695313 293.026001 100 366.66333 100 C 440.303314 100 499.996674 159.695313 499.996674 233.333313 Z"
                        />
                      </svg>
                    </div>
                  ) : (
                    <User className="w-6 h-6" />
                  )}

                  {/* User Dropdown Menu */}
                  {user && isUserMenuOpen && (
                    <div
                      className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50"
                      onMouseEnter={() => setIsUserMenuOpen(true)}
                      onMouseLeave={() => setIsUserMenuOpen(false)}
                    >
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.full_name || 'Пользователь'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {user.email}
                        </p>
                      </div>

                      <Link
                        to="/profile"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <User className="w-4 h-4 mr-2" />
                        Мой профиль
                      </Link>

                      <Link
                        to="/orders"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        Мои заказы
                      </Link>

                      <div className="border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={handleSignOut}
                          className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Выйти
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Мобильное меню */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Мобильное меню */}
          {isMobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
              <nav className="flex flex-col space-y-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-sm font-medium transition-colors duration-200 ${
                      isActive(item.href)
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            </div>
          )}
        </div>
      </header>

      {CART_ENABLED && <CartSidebar />}
      
      {/* Auth Modal */}
      {AUTH_ENABLED && (
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          initialMode={authMode}
        />
      )}
    </>
  );
};

export default Header;
